
import { Button } from "@/components/ui/button";
import { Filter } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { FilterState, MarketplaceFiltersProps } from "./types/filter-types";
import ActiveFilters from "./components/ActiveFilters";
import FilterSidebar from "./components/FilterSidebar";
import StrategySorting from "./components/StrategySorting";
import { getCampaignCookie } from "@/hooks/referrals/referralUtils";

const MarketplaceFilters = ({
  filters,
  setFilters,
  topPerformers,
  setTopPerformers,
  showMoreFilters,
  setShowMoreFilters,
  sortBy,
  onSortChange
}: MarketplaceFiltersProps) => {
  const campaignCookie = getCampaignCookie();
  const isPhemexCampaign = campaignCookie === "phemex";
  const isOkxCampaign = campaignCookie === "okx";
  
  const toggleExchange = (exchange: string) => {
    if ((isPhemexCampaign && exchange === "Phemex") || (isOkxCampaign && exchange === "OKX")) {
      return;
    }
    
    setFilters({
      ...filters,
      exchange: filters.exchange.includes(exchange) ? filters.exchange.filter(e => e !== exchange) : [...filters.exchange, exchange]
    });
  };
  
  const toggleRiskLevel = (level: string) => {
    setFilters({
      ...filters,
      riskLevel: filters.riskLevel.includes(level) ? filters.riskLevel.filter(r => r !== level) : [...filters.riskLevel, level]
    });
  };
  
  const toggleStrategyType = (type: "premium" | "free") => {
    setFilters({
      ...filters,
      strategyTypes: filters.strategyTypes.includes(type) ? filters.strategyTypes.filter(t => t !== type) : [...filters.strategyTypes, type]
    });
  };
  
  const removeFilter = (type: string, value?: string) => {
    switch (type) {
      case "minInvestment":
        setFilters({
          ...filters,
          minInvestment: "all"
        });
        break;
      case "riskLevel":
        if (value) {
          toggleRiskLevel(value);
        } else {
          setFilters({
            ...filters,
            riskLevel: []
          });
        }
        break;
      case "exchange":
        if ((isPhemexCampaign && value === "Phemex") || (isOkxCampaign && value === "OKX")) {
          return;
        }
        
        if (value) {
          toggleExchange(value);
        } else {
          let newExchanges = [];
          if (isPhemexCampaign) {
            newExchanges = ["Phemex"];
          } else if (isOkxCampaign) {
            newExchanges = ["OKX"];
          }
          setFilters({
            ...filters,
            exchange: newExchanges
          });
        }
        break;
      case "strategyType":
        if (value) {
          toggleStrategyType(value as "premium" | "free");
        }
        break;
      case "topPerformers":
        setTopPerformers(false);
        break;
    }
  };
  
  const activeFiltersCount = (filters.minInvestment !== "all" ? 1 : 0) + filters.riskLevel.length + filters.exchange.length + filters.strategyTypes.length + (topPerformers ? 1 : 0);
  
  return <div className="w-full">
      <div className="hidden sm:block sticky top-0 bg-white z-40 left-0 right-0 py-4 border-b">
        <div className="flex justify-between gap-4">
          <div className="flex-grow pt-7 py-0">
            <ActiveFilters filters={filters} topPerformers={topPerformers} removeFilter={removeFilter} />
          </div>
          
          {/* Desktop Filter Area */}
          <div className="flex items-center gap-3">
            <StrategySorting sortBy={sortBy} onSortChange={onSortChange} />
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="bg-[#1B2F47] hover:bg-[#0F1B2C] px-4 py-2 text-white items-center gap-2 whitespace-nowrap h-9">
                  <Filter className="h-4 w-4" />
                  <span>Filter{activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ''}</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:w-[400px] bg-[#E5F1FD] p-5">
                <FilterSidebar filters={filters} setFilters={setFilters} topPerformers={topPerformers} setTopPerformers={setTopPerformers} toggleExchange={toggleExchange} toggleRiskLevel={toggleRiskLevel} toggleStrategyType={toggleStrategyType} />
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Mobile Version */}
      <div className="sm:hidden mb-4">
        <ActiveFilters filters={filters} topPerformers={topPerformers} removeFilter={removeFilter} />
      </div>

      {/* Mobile Filter Button - Moved to top of screen, above navigation bar */}
      <div className="fixed z-20 top-12 left-0 right-0 sm:hidden bg-[#D3E4FD] shadow-lg border-t border-gray-200 p-4 flex items-center justify-between gap-2">
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline" className="flex-1 bg-[#1B2F47] hover:bg-[#0F1B2C] text-white flex items-center justify-center gap-2">
              <Filter className="h-4 w-4" />
              <span>Filter{activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ''}</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-full sm:w-[400px] bg-[#E5F1FD] p-5">
            <FilterSidebar filters={filters} setFilters={setFilters} topPerformers={topPerformers} setTopPerformers={setTopPerformers} toggleExchange={toggleExchange} toggleRiskLevel={toggleRiskLevel} toggleStrategyType={toggleStrategyType} />
          </SheetContent>
        </Sheet>
        <StrategySorting sortBy={sortBy} onSortChange={onSortChange} />
      </div>
    </div>;
};

export default MarketplaceFilters;
